import * as React from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BackgroundImage,
  Box,
  Flex,
  Image,
  PrimaryButton,
  Text,
} from '@leagueplatform/genesis-commons';
import {
  TextAction,
  StackLayout,
  queryHelpers,
  useMediaQuery,
  Box as GenesisBox,
} from '@leagueplatform/genesis-core';
import { useAuth } from '@leagueplatform/auth-utils';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { useIntl } from '@leagueplatform/locales';
import landingPageImage from 'assets/landing-page-image.jpg';
import appleBadge from 'assets/mobile-app-icons/apple-badge.svg';
import googleBadge from 'assets/mobile-app-icons/google-badge.svg';
import { LoadingSpinner } from 'components/spinner/loading-spinner.component';
import { captureAuthenticationError } from 'utils/sentry-helpers';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';

type AppDownloadBadgesProps = {
  appStoreUrl: string;
  playStoreUrl: string;
};

type LandingPageProps = {
  logo: string;
  landingGreeting: string;
  buttonText: string;
  authErrorText: string;
  footer?: React.ReactNode;
  appDownloadBadges?: AppDownloadBadgesProps;
};

const trackAppDownloadBadgeClick = (badgeName: string) => {
  trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
    product_area: 'foundation',
    sub_product_area: 'app download',
    screen_name: 'login screen',
    detail: 'navigate to app store',
    content: { name: badgeName },
  });
};

// Used for calculating the top padding of the content area when app download badges are present.
const APP_BADGE_HEIGHT = '43px';
const LANDING_CONTENT_SPACING = '$four';

const AppDownloadBadges = ({
  appStoreUrl,
  playStoreUrl,
}: AppDownloadBadgesProps) => {
  const { formatMessage } = useIntl();
  const appStoreAlt = formatMessage({ id: 'DISCLAIMER_CTA_APPSTORE' });
  const playStoreAlt = formatMessage({ id: 'DISCLAIMER_CTA_PLAYSTORE' });

  return (
    <StackLayout
      horizontalAlignment="center"
      verticalAlignment="center"
      orientation="horizontal"
      spacing="$one"
      css={{ flex: '0', flexWrap: 'wrap' }}
    >
      <TextAction
        href={appStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackAppDownloadBadgeClick('App Store')}
      >
        <GenesisBox
          as="img"
          src={appleBadge}
          alt={appStoreAlt}
          css={{
            height: APP_BADGE_HEIGHT,
            width: 'auto',
          }}
        />
      </TextAction>
      <TextAction
        href={playStoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackAppDownloadBadgeClick('Google Play')}
      >
        <GenesisBox
          as="img"
          src={googleBadge}
          alt={playStoreAlt}
          css={{
            height: APP_BADGE_HEIGHT,
            width: 'auto',
          }}
        />
      </TextAction>
    </StackLayout>
  );
};

const LandingImageBox = () => (
  <Box flexDirection="column" flex={{ _: '0', tablet: '1' }}>
    <BackgroundImage
      alt=""
      src={landingPageImage}
      role="presentation"
      data-testid="background-image"
      objectFit="cover"
      height={{ _: '40vh', tablet: '100vh' }}
      backgroundPosition={{ _: '50% 10%', tablet: '0 top' }}
    />
  </Box>
);

export const LandingPage = ({
  logo,
  landingGreeting,
  buttonText,
  authErrorText,
  footer,
  appDownloadBadges,
}: LandingPageProps) => {
  const navigate = useNavigate();
  const { isAuthenticated, error } = useAuth();
  const isAboveTablet = useMediaQuery(queryHelpers.up('tablet'));
  // Note: Post-authentication navigation is primarily handled by useAuthLoginRedirect hook
  // in AuthSessionContainer. This provides a fallback to ensure users don't get stuck.
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ pathname: APP_MANAGED_ROUTES.HOME });
    }
  }, [navigate, isAuthenticated]);

  useEffect(() => {
    if (error) {
      captureAuthenticationError(error);
    }
  }, [error]);

  return (
    <Box
      as="main"
      position="relative"
      flex="1"
      textAlign="center"
      minHeight="100vh"
    >
      {isAuthenticated === undefined ? (
        <LoadingSpinner />
      ) : (
        <Flex height="100vh" flexDirection={{ _: 'column', tablet: 'row' }}>
          <LandingImageBox />
          <StackLayout
            orientation="vertical"
            horizontalAlignment="center"
            verticalAlignment="center"
            spacing={appDownloadBadges ? LANDING_CONTENT_SPACING : '0'}
            css={{ overflowX: isAboveTablet ? 'auto' : 'visible' }}
          >
            <StackLayout
              orientation="vertical"
              horizontalAlignment="center"
              verticalAlignment="center"
              css={{
                maxWidth: '350px',
                flex: '1',
                margin: `1rem auto`,
                paddingTop:
                  isAboveTablet && appDownloadBadges
                    ? `calc(${APP_BADGE_HEIGHT} + ${LANDING_CONTENT_SPACING} + ${LANDING_CONTENT_SPACING})`
                    : `${appDownloadBadges ? LANDING_CONTENT_SPACING : '0'}`,
              }}
            >
              <Image
                src={logo}
                alt="logo"
                maxHeight={{ _: '100px', phone: '120px' }}
              />
              <Text fontSize="body1" marginY="twoAndHalf" paddingX="35px">
                {landingGreeting}
              </Text>
              {isAuthenticated === false && (
                <PrimaryButton
                  maxWidth="327px"
                  width="100%"
                  alignSelf="center"
                  onClick={() => navigate(APP_MANAGED_ROUTES.SIGN_IN)}
                >
                  {buttonText}
                </PrimaryButton>
              )}
              {error && (
                <Text color="onSurface.text.critical">{authErrorText}</Text>
              )}
            </StackLayout>
            {appDownloadBadges && (
              <AppDownloadBadges
                appStoreUrl={appDownloadBadges.appStoreUrl}
                playStoreUrl={appDownloadBadges.playStoreUrl}
              />
            )}
            <Box
              paddingBottom={{ _: 'two', tablet: 'three', desktop: 'four' }}
              width="100%"
              margin="0 auto"
            >
              {footer}
            </Box>
          </StackLayout>
        </Flex>
      )}
    </Box>
  );
};
